import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { GetJson } from '../helpers/fetchReact';
import {
  REST_API,
  STATE_STATUS,
  INI_START_DATE,
  INI_END_DATE,
  DATASET_TYPES,
  ADDITIONAL_DATA_DIRECTION,
  SENSOR_PLACEMENT_CONFIG,
  FROST_PROBE_CONFIG,
  SNOW_POLE_CONFIG,
  DATA_RANGE,
  DATASET_CONFIG,
} from '@awp/constants';
import {
  isValueEmpty,
  parseDatasetId,
  sortFrostProbeTypeDropDown,
  convertRelElevationTocm,
  fieldSorter,
  isWxnetDataset,
} from '@awp/helpers/utilsReact';
import { setHours, setMinutes, setSeconds, subDays, addDays, format as formatDate } from 'date-fns';
import { date } from 'yup';

const initialState = {
  observationTypes: [],
  validationRules: [],
  data: null,
  multiStationData: null,
  weatherStationId: null,
  weatherStation: null,
  viewableGraphDates: {
    startDate: {
      value: new Date(new Date(INI_START_DATE)).getTime(),
      error: '',
      disabled: false,
    },
    endDate: {
      value: Date.now(),
      error: '',
      disabled: false,
    },
  },
  startDate: {
    value: new Date(new Date(INI_START_DATE)).getTime(),
    error: '',
    disabled: false,
  },
  endDate: {
    value: new Date(new Date(INI_END_DATE)).getTime(),
    error: '',
    disabled: false,
  },
  issuedBy: '-1',
  weatherForecastId: '',
  weatherForecast: {},
  weatherForecasts: [],
  textDescription: {},
  status: {
    observationTypes: STATE_STATUS.IDLE,
    validationRules: STATE_STATUS.IDLE,
    weatherForecast: STATE_STATUS.IDLE,
    weatherForecasts: STATE_STATUS.IDLE,
    textDescription: STATE_STATUS.IDLE,
    data: STATE_STATUS.IDLE,
    multiStationData: STATE_STATUS.IDLE,
    additionalData: STATE_STATUS.IDLE,
    additionalMultiStationData: STATE_STATUS.IDLE,
  },
  error: null,
  showConfidenceLevel: true,
  showInvalidData: true,
  showValidData: true,
  dataRange: DATA_RANGE.DEFAULT,
  displayDateRange: { endDate: new Date().getTime(), displayRange: DATA_RANGE.DEFAULT }, //filter Endate and Data Range
};

export const selectObservationTypes = (state) => state.dataVisualization.observationTypes;
export const fetchObservationTypes = createAsyncThunk('dataVisualization/observationtypes', async () => {
  const url = REST_API.DATA_VISUALIZATION_OBSERVATION_TYPES;
  const response = await GetJson(url);
  return await response.json();
});

export const derivedObservatioIds = (state) => state.dataVisualization.derivedObservationIds;
export const fetchDerivedObservationIds = createAsyncThunk('dataVisualization/derivedObservationIds', async () => {
  try {
    const url = REST_API.DERIVED_OBSERVATION;
    const response = await GetJson(url);
    return await response.json();
  } catch {
    return [];
  }
});

export const selectValidationRules = (state) => state.dataVisualization.validationRules;
export const fetchValidationRules = createAsyncThunk('dataVisualization/validationRules', async () => {
  const url = REST_API.DATA_VISUALIZATION_VALIDATION_RULES;
  const response = await GetJson(url);
  return await response.json();
});

export const selectWeatherStationId = (state) => {
  const id = state.dataVisualization.weatherStationId;
  return id ?? -1;
};
export const selectPresetId = (state) => {
  const id = state.dataVisualization.presetId;
  return id ?? -999;
};
export const selectPreset = (state) => state.dataVisualization.preset;
export const selectWeatherStation = (state) => state.dataVisualization.weatherStation;
export const selectStartDate = (state) => state.dataVisualization.startDate;
export const selectEndDate = (state) => state.dataVisualization.endDate;
export const selectStatus = (state) => state.dataVisualization.status;
export const selectShowConfidenceLevel = (state) => state.dataVisualization.showConfidenceLevel;
export const selectShowInvalidData = (state) => state.dataVisualization.showInvalidData;
export const selectShowValidData = (state) => state.dataVisualization.showValidData;
export const selectDataRange = (state) => state.dataVisualization.dataRange;
export const selectDisplayDateRange = (state) => state.dataVisualization.displayDateRange; //filter Endate and Data Range

//forecast
export const selectIssuedBy = (state) => state.dataVisualization.issuedBy;
export const selectWeatherForecastId = (state) => state.dataVisualization.weatherForecastId;
export const selectWeatherForecasts = (state) => state.dataVisualization.weatherForecasts;
export const selectTextDescription = (state) => state.dataVisualization.textDescription;
export const selectWeatherForecast = (state) => state.dataVisualization.weatherForecast;

export const selectData = (state) => state.dataVisualization.data;
export const selectMultiStationData = (state) => state.dataVisualization.multiStationData;

export const multiStationDataStart = (state) => state.dataVisualization.multiStationData?.StartDate;
export const multiStationDataEnd = (state) => state.dataVisualization.multiStationData?.EndDate;
export const getViewableGraphDates = (state) => state.dataVisualization.viewableGraphDates;

export const selectHeatMapObservation = (state) =>
  state.dataVisualization.observationTypes.find(
    (obs) => obs.ObservationTypeName === FROST_PROBE_CONFIG.FROST_PROBE_OBS_NAME,
  );

export const selectSnowPoleObservation = (state) =>
  state.dataVisualization.observationTypes.find(
    (obs) => obs.ObservationTypeName === SNOW_POLE_CONFIG.SNOW_POLE_OBS_NAME,
  );

export const selectPrecisionResistorObservation = (state) =>
  state.dataVisualization.observationTypes.find(
    (obs) => obs.ObservationTypeName === FROST_PROBE_CONFIG.PRECISN_RESISTOR,
  );

export const fetchData = createAsyncThunk(
  'dataVisualization',
  async (args, { getState }) => {
    const {
      dataVisualization: {
        weatherStationId,
        startDate: stateStartDate,
        endDate: stateEndDate,
        observationTypes,
        validationRules,
      },
    } = getState();

    let apiStartDate;
    let apiEndDate;

    if (args?.dataRange) {
      // Calculate apiStartDate and apiEndDate based on passed dataRange
      const { endDate, displayRange } = args.dataRange;

      apiStartDate = formatDate(
        setSeconds(setMinutes(subDays(new Date(endDate), displayRange), 0), 0).getTime(),
        'yyyy-MM-dd-HH-mm-ss',
      );

      apiEndDate = formatDate(setSeconds(setMinutes(new Date(endDate), 59), 0).getTime(), 'yyyy-MM-dd-HH-mm-ss');
    } else {
      // Use state values if dataRange is not passed
      apiStartDate = formatDate(stateStartDate.value, 'yyyy-MM-dd-HH-mm-ss');
      apiEndDate = formatDate(stateEndDate.value, 'yyyy-MM-dd-HH-mm-ss');
    }

    const url = `${REST_API.DATA_VISUALIZATION}${weatherStationId}/${apiStartDate}/${apiEndDate}`;
    const response = await GetJson(url);
    const data = await response.json();
    addAttributesAndSort(data, observationTypes, validationRules);

    return data;
  },
  {
    condition: (args, { getState }) => {
      const {
        dataVisualization: { weatherStationId, startDate: stateStartDate, endDate: stateEndDate, data },
      } = getState();

      let apiStartDate;
      let apiEndDate;

      if (args?.forceFetch) return true;

      if (isValueEmpty(data)) return true;

      if (args?.dataRange) {
        // Calculate apiStartDate and apiEndDate based on passed dataRange
        const { endDate, displayRange } = args.dataRange;

        apiStartDate = formatDate(
          setSeconds(setMinutes(subDays(new Date(endDate), displayRange), 0), 0).getTime(),
          'yyyy-MM-dd-HH-mm-ss',
        );

        apiEndDate = formatDate(setSeconds(setMinutes(new Date(endDate), 59), 0).getTime(), 'yyyy-MM-dd-HH-mm-ss');
      } else {
        // Use state values if dataRange is not passed
        apiStartDate = formatDate(stateStartDate.value, 'yyyy-MM-dd-HH-mm-ss');
        apiEndDate = formatDate(stateEndDate.value, 'yyyy-MM-dd-HH-mm-ss');
      }

      return (
        weatherStationId !== data.WeatherStationId || apiStartDate !== data.StartDate || apiEndDate !== data.EndDate
      );
    },
  },
);

export const fetchMultiStationData = createAsyncThunk(
  'multiDataVisualization',
  async ({ weatherStationIds, weatherStationId, presetId, endDate, displayRange, forceFetch }, { getState }) => {
    const {
      dataVisualization: { observationTypes, validationRules },
    } = getState();
    const apiStartDate = formatDate(
      setSeconds(setMinutes(subDays(new Date(endDate), displayRange), 0), 0).getTime(),
      'yyyy-MM-dd-HH-mm-ss',
    );
    //const apiStartDate = formatDate(startDate.value, 'yyyy-MM-dd-HH-mm-ss');
    const apiEndDate = formatDate(setSeconds(setMinutes(new Date(endDate), 59), 0).getTime(), 'yyyy-MM-dd-HH-mm-ss');

    let url = `${REST_API.MULTI_DATA_VISUALIZATION}`;
    url += `${weatherStationIds.join(',')}/${presetId}/${apiStartDate}/${apiEndDate}`;

    const response = await GetJson(url);
    let data = await response.json();

    //data comes back with multiple DTOs per station
    data.DataVisualizationDTOs = data.DataVisualizationDTOs.map((d) => {
      addAttributesAndSort(d, observationTypes, validationRules); //mutates the dataset
      return d;
      //return { ...d, WeatherStation: weatherStation };
    });
    data.PresetId = presetId;
    data.DeferWeatherStationId = weatherStationId;
    return data;
  },
  {
    condition: (args, { getState }) => {
      const {
        dataVisualization: {
          weatherStationId,
          startDate: stateStartDate,
          endDate: stateEndDate,
          multiStationData,
          presetId,
        },
      } = getState();
      console.log(args);
      console.log(multiStationData);

      let apiStartDate;
      let apiEndDate;

      if (args?.forceFetch) return true;

      if (isValueEmpty(multiStationData)) return true;

      if (args?.endDate) {
        // Calculate apiStartDate and apiEndDate based on passed dataRange
        const { endDate, displayRange } = args;

        apiStartDate = formatDate(
          setSeconds(setMinutes(subDays(new Date(endDate), displayRange), 0), 0).getTime(),
          'yyyy-MM-dd-HH-mm-ss',
        );

        apiEndDate = formatDate(setSeconds(setMinutes(new Date(endDate), 59), 0).getTime(), 'yyyy-MM-dd-HH-mm-ss');
      } else {
        // Use state values if dataRange is not passed
        apiStartDate = formatDate(stateStartDate.value, 'yyyy-MM-dd-HH-mm-ss');
        apiEndDate = formatDate(stateEndDate.value, 'yyyy-MM-dd-HH-mm-ss');
      }

      return (
        args.presetId !== multiStationData.PresetId ||
        args.weatherStationId !== multiStationData.DeferWeatherStationId ||
        apiStartDate !== multiStationData.StartDate ||
        apiEndDate !== multiStationData.EndDate
      );
    },
  },
);

export const fetchAdditionalMultiStationData = createAsyncThunk(
  'additionalMultiDataVisualization',
  async ({ weatherStationIds, direction, forceFetch }, { getState }) => {
    const {
      dataVisualization: { startDate, endDate, observationTypes, validationRules, dataRange },
    } = getState();

    const apiStartDate = formatDate(
      setSeconds(
        setMinutes(
          setHours(
            direction === 'backward'
              ? subDays(new Date(startDate.value), dataRange)
              : addDays(new Date(endDate.value), 1),
            0,
          ),
          0,
        ),
        0,
      ).getTime(),
      'yyyy-MM-dd-HH-mm-ss',
    );

    const apiEndDate = formatDate(
      direction === 'backward'
        ? setSeconds(setMinutes(setHours(subDays(new Date(startDate.value), 1), 23), 59), 0).getTime()
        : setSeconds(setMinutes(setHours(addDays(new Date(endDate.value), dataRange), 23), 59), 0).getTime(),
      'yyyy-MM-dd-HH-mm-ss',
    );
    const url = `${REST_API.MULTI_DATA_VISUALIZATION}${weatherStationIds.join(',')}/${apiStartDate}/${apiEndDate}`;
    const response = await GetJson(url);
    const data = await response.json();

    data.DataVisualizationDTOs = data.DataVisualizationDTOs.map((d) => {
      //const weatherStation = getWeatherStationById(getState(), d.WeatherStationId);
      addAttributesAndSort(d, observationTypes, validationRules); //mutates the dataset
      return d;
      //return { ...d, WeatherStation: weatherStation };
    });
    //return data;

    //addAttributesAndSort(data, observationTypes, validationRules);
    return { result: data, direction: direction };
  },
);
// Get a list of issued times
export const fetchWeatherForecasts = createAsyncThunk(
  'dataVisualization/weatherForecasts',
  async (args, { getState }) => {
    // Get state values
    const {
      dataVisualization: { weatherStationId, startDate, endDate },
    } = getState();
    // Use args if provided, otherwise fall back to state values
    const selectedWeatherStationId = args?.weatherStationId || weatherStationId;
    const selectedStartDate = args?.isMultiStation ? args?.startDate : startDate.value;
    const selectedEndDate = args?.isMultiStation ? args?.endDate : endDate.value;

    const apiStartDate = formatDate(addDays(selectedStartDate, 1), 'yyyy-MM-dd-HH-mm');
    const apiEndDate = formatDate(selectedEndDate, 'yyyy-MM-dd-HH-mm');

    const url = `${REST_API.FORECAST_ISSUED_TIMES}${selectedWeatherStationId}/${apiStartDate}/${apiEndDate}`;
    const res = await GetJson(url);
    let forecasts = await res.json();
    if (!Array.isArray(forecasts)) return forecasts;

    forecasts.forEach((forecast) => {
      forecast.selectedStartDate = selectedStartDate;
      forecast.selectedEndDate = selectedEndDate;
    });
    return forecasts;
  },
  {
    condition: (args, { getState }) => {
      const {
        dataVisualization: { weatherForecastId, weatherForecast, weatherForecasts },
      } = getState();
      console.log(args);
      console.log(weatherForecast);
      console.log(weatherForecasts);
      if (weatherForecasts && Array.isArray(weatherForecasts) && weatherForecasts.length > 0) {
        console.log(
          weatherForecast &&
            weatherForecast.WeatherStationId === args.weatherStationId &&
            //weatherForecasts[0].WeatherStationId === args.weatherStationId &&
            weatherForecasts[0].selectedStartDate === args.startDate &&
            weatherForecasts[0].selectedEndDate === args.endDate,
        );
        if (
          weatherForecast &&
          weatherForecast.WeatherStationId === args.weatherStationId &&
          //weatherForecasts[0].WeatherStationId === args.weatherStationId &&
          weatherForecasts[0].selectedStartDate === args.startDate &&
          weatherForecasts[0].selectedEndDate === args.endDate
        ) {
          console.log(10);
          return false;
        }
        console.log(11);
      }
      console.log(12);
      const selectedWeatherForecastId = args?.weatherForecastId || weatherForecastId;
      console.log(selectedWeatherForecastId);

      if (isValueEmpty(selectedWeatherForecastId)) return true;
      if (weatherForecast && weatherForecast.ForecastId === selectedWeatherForecastId) return false;
      return true;
    },
  },
);

export const fetchWeatherForecastText = createAsyncThunk(
  'dataVisualization/textDescription',
  async (args, { getState }) => {
    const {
      dataVisualization: { weatherForecastId },
    } = getState();
    // Use args if provided, otherwise fall back to state values
    const selectedWeatherForecastId = args?.weatherForecastId || weatherForecastId;

    const url = `${REST_API.FORECAST_ISSUED_TEXT}${selectedWeatherForecastId}`;
    const res = await GetJson(url);
    return await res.json();
  },
  {
    condition: (args, { getState }) => {
      const {
        dataVisualization: { weatherForecastId, weatherForecast },
      } = getState();
      console.log(args);
      console.log(weatherForecast);
      const selectedWeatherForecastId = args?.weatherForecastId || weatherForecastId;

      if (isValueEmpty(selectedWeatherForecastId)) return false;
      if (weatherForecast && weatherForecast.ForecastId === selectedWeatherForecastId) return false;
      return true;
    },
  },
);

export const fetchWeatherForecast = createAsyncThunk(
  'dataVisualization/weatherForecast',
  async (args, { getState }) => {
    const {
      dataVisualization: { weatherForecastId, weatherStationId, observationTypes, validationRules },
    } = getState();
    // Use weatherForecastId from args if provided, otherwise fall back to state value
    const selectedWeatherForecastId = args?.weatherForecastId || weatherForecastId;
    const isMultiStation = args?.isMultiStation || false;

    const url = `${REST_API.DATA_VISUALIZATION_ISSUED_WEATHERNET}${selectedWeatherForecastId}`;
    const res = await GetJson(url);
    const data = await res.json();
    addAttributesAndSort(data, observationTypes, validationRules);
    //const selectedWeatherStationId = args?.weatherStationId; // || weatherStationId || data?.WeatherStationId;
    // Return data along with isMultiStation flag
    return {
      data,
      isMultiStation,
      weatherStationId: args?.weatherStationId, // return selected wsId from the forecast panel
      startDate: args?.startDate,
      endDate: args?.endDate,
    };
  },
  {
    condition: (args, { getState }) => {
      const {
        dataVisualization: { weatherForecastId, weatherForecast },
      } = getState();

      const selectedWeatherForecastId = args?.weatherForecastId || weatherForecastId;
      console.log(weatherForecastId);
      console.log(args);
      console.log(weatherForecast);
      if (isValueEmpty(selectedWeatherForecastId)) return false;
      if (weatherForecast && weatherForecast.ForecastId === selectedWeatherForecastId) return false;
      return true;
    },
  },
);

export const fetchAdditionalData = createAsyncThunk('additionalDataVisualization', async (args, { getState }) => {
  const {
    dataVisualization: { weatherStationId, startDate, endDate, observationTypes, validationRules, dataRange },
  } = getState();

  const apiStartDate = formatDate(
    setSeconds(
      setMinutes(
        setHours(
          args.direction === 'backward'
            ? subDays(new Date(startDate.value), dataRange)
            : addDays(new Date(endDate.value), 1),
          0,
        ),
        0,
      ),
      0,
    ).getTime(),
    'yyyy-MM-dd-HH-mm-ss',
  );

  const apiEndDate = formatDate(
    args.direction === 'backward'
      ? setSeconds(setMinutes(setHours(subDays(new Date(startDate.value), 1), 23), 59), 0).getTime()
      : setSeconds(setMinutes(setHours(addDays(new Date(endDate.value), dataRange), 23), 59), 0).getTime(),
    'yyyy-MM-dd-HH-mm-ss',
  );

  const url = `${REST_API.DATA_VISUALIZATION}${weatherStationId}/${apiStartDate}/${apiEndDate}`;
  const response = await GetJson(url);
  const data = await response.json();
  addAttributesAndSort(data, observationTypes, validationRules);
  return { result: data, direction: args.direction };
});

export const dataVisualizationSlice = createSlice({
  name: 'dataVisualization',
  initialState,
  reducers: {
    setDataRange(state, action) {
      state.dataRange = action.payload.value;
    },
    setPreset(state, action) {
      state.presetId = action.payload?.value;
      state.preset = action.payload;
    },
    clearPreset(state) {
      state.presetId = null;
      state.preset = null;
    },
    setWeatherStation(state, action) {
      state.weatherStationId = action.payload.Id;
      state.weatherStation = action.payload;
    },
    clearWeatherStation(state) {
      state.weatherStationId = null;
      state.weatherStation = null;
    },
    clearData(state) {
      state.data = null;
      state.status.data = STATE_STATUS.IDLE;
    },
    setStartDate(state, action) {
      state.startDate = action.payload;
    },
    setEndDate(state, action) {
      state.endDate = action.payload;
    },
    setDates(state, action) {
      state.startDate = action.payload.startDate;
      state.endDate = action.payload.endDate;
    },
    setDataDateRange(state, action) {
      state.endDate = action.payload.endDate;
      state.dataRange = action.payload.dataRange;
    },
    setDisplayDateRange(state, action) {
      state.displayDateRange = action.payload;
    },
    setShowConfidenceLevel(state, action) {
      state.showConfidenceLevel = action.payload;
    },
    setShowInvalidData(state, action) {
      state.showInvalidData = action.payload;
    },
    setShowValidData(state, action) {
      state.showValidData = action.payload;
    },
    setWeatherForecastId(state, action) {
      state.weatherForecastId = action.payload;
    },
    setIssuedBy(state, action) {
      state.issuedBy = action.payload;
    },
    setWeatherForecasts(state, action) {
      state.weatherForecasts = action.payload;
    },
    setTextDescription(state, action) {
      state.textDescription = action.payload;
    },
    setViewableGraphDates(state, action) {
      state.viewableGraphDates = action.payload;
    },
  },
  extraReducers: {
    [fetchObservationTypes.pending]: (state) => {
      state.status.observationTypes = STATE_STATUS.LOADING;
    },
    [fetchObservationTypes.fulfilled]: (state, action) => {
      state.observationTypes = action.payload;
      state.status.observationTypes = STATE_STATUS.SUCCESS;
    },
    [fetchObservationTypes.rejected]: (state) => {
      state.status.observationTypes = STATE_STATUS.FAILED;
    },

    [fetchDerivedObservationIds.pending]: (state) => {
      state.status.derivedObservationIds = STATE_STATUS.LOADING;
    },
    [fetchDerivedObservationIds.fulfilled]: (state, action) => {
      state.derivedObservationIds = action.payload;
      state.status.derivedObservationIds = STATE_STATUS.SUCCESS;
    },
    [fetchDerivedObservationIds.rejected]: (state) => {
      state.status.derivedObservationIds = STATE_STATUS.FAILED;
    },

    [fetchValidationRules.pending]: (state) => {
      state.status.validationRules = STATE_STATUS.LOADING;
    },
    [fetchValidationRules.fulfilled]: (state, action) => {
      state.validationRules = action.payload;
      state.status.validationRules = STATE_STATUS.SUCCESS;
    },
    [fetchValidationRules.rejected]: (state) => {
      state.status.validationRules = STATE_STATUS.FAILED;
    },
    [fetchWeatherForecastText.pending]: (state) => {
      state.status.textDescription = STATE_STATUS.LOADING;
    },
    [fetchWeatherForecastText.fulfilled]: (state, action) => {
      state.textDescription = action.payload;
      state.status.textDescription = STATE_STATUS.SUCCESS;
    },
    [fetchWeatherForecastText.rejected]: (state) => {
      state.status.textDescription = STATE_STATUS.FAILED;
    },
    [fetchWeatherForecast.pending]: (state) => {
      state.status.weatherForecast = STATE_STATUS.LOADING;
    },
    [fetchWeatherForecast.fulfilled]: (state, action) => {
      state.status.weatherForecast = STATE_STATUS.SUCCESS;
      const { data, isMultiStation, weatherStationId, startDate, endDate } = action.payload;
      const apiStartDate = isMultiStation ? formatDate(startDate, 'yyyy-MM-dd-HH-mm-ss') : '';
      //const apiStartDate = formatDate(startDate.value, 'yyyy-MM-dd-HH-mm-ss');
      const apiEndDate = isMultiStation ? formatDate(endDate, 'yyyy-MM-dd-HH-mm-ss') : '';
      state.weatherForecast = data;
      state.textDescription = {
        Id: data.ForecastId,
        Forecaster: data.Forecaster,
        Confidence: data.Confidence,
        WeatherTextForecastTimeDays: data.WeatherTextForecastTimeDays,
        HourlyStart: data.HourlyStart,
        HourlyEnd: data.HourlyEnd,
      };
      if (data.Datasets && data.Datasets.length > 0) {
        //find the DataVisualizationDTO
        if (
          state.multiStationData &&
          state.multiStationData.DataVisualizationDTOs &&
          state.multiStationData.DataVisualizationDTOs.length > 0
        ) {
          // Iterate through the action payload Datasets to clean all weatherNet Forecast datasets (the graph component) for all stations
          state.multiStationData.DataVisualizationDTOs = state.multiStationData.DataVisualizationDTOs?.filter(
            (dvDTO) => {
              if (dvDTO.Datasets && Array.isArray(dvDTO.Datasets)) {
                // Filter out datasets that meet isWxnetDataset condition
                dvDTO.Datasets = dvDTO.Datasets.filter((ds) => !isWxnetDataset(ds));
              }
              return dvDTO;
            },
          );
          const dvDTOIdx = state.multiStationData?.DataVisualizationDTOs?.findIndex((dto) => {
            return dto.WeatherStationId === weatherStationId;
          });
          if (dvDTOIdx >= 0) {
            data.Datasets?.forEach((newDataset) => {
              const payloadDatasetId = newDataset.DatasetId;

              // Find index of the dataset in the current WeatherStationId
              const idx = state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets?.findIndex(
                (existingDataset) => existingDataset.DatasetId === payloadDatasetId,
              );

              // Find and remove the dataset from other WeatherStationIds if it exists
              state.multiStationData.DataVisualizationDTOs.forEach((dto, index) => {
                if (index !== dvDTOIdx) {
                  const otherIdx = dto.Datasets?.findIndex((dataset) => dataset.DatasetId === payloadDatasetId);
                  if (otherIdx > -1) {
                    // Remove the dataset if it exists for a different WeatherStationId
                    dto.Datasets?.splice(otherIdx, 1);
                  }
                }
              });

              const datasetToAppend = { ...newDataset };

              // Immutably update or add the dataset in the matching WeatherStationId
              if (idx > -1) {
                // Dataset exists, so replace it with the new data
                state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets[idx] = datasetToAppend;
              } else {
                // No matching dataset, so push the new one
                if (state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets) {
                  state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets.push(datasetToAppend);
                } else {
                  state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets = [];
                  state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets.push(datasetToAppend);
                }
              }
            });
          } else {
            //new datasets which is not in state.multiStationData.DataVisualizationDTOs
            if (data && data.Datasets && data.Datasets.length > 0) {
              data.Datasets?.forEach((newDataset) => {
                const payloadDatasetId = newDataset.DatasetId;
                // Find and remove the dataset from other WeatherStationIds if it exists
                state.multiStationData.DataVisualizationDTOs.forEach((dto, index) => {
                  const otherIdx = dto.Datasets?.findIndex((dataset) => dataset.DatasetId === payloadDatasetId);
                  if (otherIdx > -1) {
                    // Remove the dataset if it exists for a different WeatherStationId
                    dto.Datasets?.splice(otherIdx, 1);
                  }
                });
              });
              state.multiStationData.WeatherStationIds.push(weatherStationId || data.WeatherStationId);
              state.multiStationData.DataVisualizationDTOs.push({
                WeatherStationId: weatherStationId || data.WeatherStationId,
                Datasets: data.Datasets,
                StartDate: null,
                EndDate: null,
                ObservationLastUpdate: null,
                ValidationLastUpdate: null,
              });
            }
          }
        } else if (
          !state.multiStationData ||
          !state.multiStationData.DataVisualizationDTOs ||
          state.multiStationData.DataVisualizationDTOs.length == 0
        ) {
          let _wss = [];
          _wss.push(weatherStationId || data.WeatherStationId);
          state.multiStationData = {
            WeatherStationIds: _wss,
            StartDate: apiStartDate,
            EndDate: apiEndDate,
            DataVisualizationDTOs: [],
            QueryTime: null,
          };
          state.multiStationData.DataVisualizationDTOs.push({
            WeatherStationId: weatherStationId || data.WeatherStationId,
            Datasets: data.Datasets,
            StartDate: null,
            EndDate: null,
            ObservationLastUpdate: null,
            ValidationLastUpdate: null,
          });
        }
      }
    },
    [fetchWeatherForecast.rejected]: (state, action) => {
      state.status.weatherForecast = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchWeatherForecasts.pending]: (state) => {
      state.status.weatherForecasts = STATE_STATUS.LOADING;
    },
    [fetchWeatherForecasts.fulfilled]: (state, action) => {
      state.status.weatherForecasts = STATE_STATUS.SUCCESS;
      state.weatherForecasts = action.payload;
      state.textDescription = {};
      state.weatherForecast = {};
    },
    [fetchWeatherForecasts.rejected]: (state, action) => {
      state.status.weatherForecasts = STATE_STATUS.FAILED;
      state.error = action.error.message;
    },
    [fetchData.pending]: (state) => {
      state.status.data = STATE_STATUS.LOADING;
    },
    [fetchData.fulfilled]: (state, action) => {
      state.data = action.payload;
      state.status.data = STATE_STATUS.SUCCESS;
    },
    [fetchData.rejected]: (state) => {
      state.status.data = STATE_STATUS.FAILED;
    },
    [fetchMultiStationData.pending]: (state) => {
      state.status.multiStationData = STATE_STATUS.LOADING;
    },
    [fetchMultiStationData.fulfilled]: (state, action) => {
      const multiStationData = action.payload;

      //Check if the current state.multiStationData already includes forecast datasets
      //before overriding it with new data, as fetching the WXNet forecast comes from a different API call.
      if (
        state.multiStationData &&
        state.multiStationData.DataVisualizationDTOs &&
        state.multiStationData.DataVisualizationDTOs.length > 0
      ) {
        if (
          multiStationData &&
          multiStationData.DataVisualizationDTOs &&
          multiStationData.DataVisualizationDTOs.length > 0
        ) {
          //find wxnet data from the existing multiStationData.DataVisualizationDTOs as they are fetched from a different api
          let wxNetDataSets = state.multiStationData.DataVisualizationDTOs.flatMap((dv) => dv.Datasets)?.filter(
            (fds) => fds.type === DATASET_TYPES.ForecastData && fds.sensorTypeName === DATASET_CONFIG.WxNetPavement,
          );

          state.multiStationData = multiStationData; //override data
          if (wxNetDataSets) {
            //add wxnet datasets back to multiStationData.DataVisualizationDTOs
            wxNetDataSets?.forEach((newDataset) => {
              const wxnetWeatherStationId = newDataset.weatherStationId;
              const dvDTOIdx = state.multiStationData?.DataVisualizationDTOs?.findIndex((dto) => {
                return dto.WeatherStationId === wxnetWeatherStationId;
              });
              const datasetToAppend = { ...newDataset };
              if (dvDTOIdx > -1) {
                //append to the existing dto
                if (state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets) {
                  state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets.push(datasetToAppend);
                } else {
                  state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets = [];
                  state.multiStationData.DataVisualizationDTOs[dvDTOIdx].Datasets.push(datasetToAppend);
                }
              } else {
                //add a new dto
                let _datasets = [];
                _datasets.push(datasetToAppend);
                state.multiStationData.WeatherStationIds.push(wxnetWeatherStationId);
                state.multiStationData.DataVisualizationDTOs.push({
                  WeatherStationId: wxnetWeatherStationId,
                  Datasets: _datasets,
                  StartDate: null,
                  EndDate: null,
                  ObservationLastUpdate: null,
                  ValidationLastUpdate: null,
                });
              }
            });
          }
        }
      } else {
        state.multiStationData = multiStationData;
      }
      state.status.multiStationData = STATE_STATUS.SUCCESS;
    },
    [fetchMultiStationData.rejected]: (state) => {
      state.status.multiStationData = STATE_STATUS.FAILED;
    },
    [fetchAdditionalData.pending]: (state) => {
      state.status.additionalData = STATE_STATUS.LOADING;
    },
    [fetchAdditionalData.fulfilled]: (state, action) => {
      const direction = action.payload.direction;
      //iterate thru the action payload Datasets, find the corresponding Dataset in the state.data.Dataset
      action.payload.result.Datasets.forEach((element) => {
        const payloadDatasetId = element.DatasetId;
        const idx = state.data.Datasets.findIndex((ds) => {
          return ds.DatasetId === payloadDatasetId;
        });
        // if the dataset from the payload doesn't exist then the idx is -1, in that case
        // we should add the dataset to the state.data
        if (idx !== -1) {
          const data1 = state.data.Datasets[idx].Data || [];
          const data2 = element.Data || [];
          const merged = data1.concat(data2).sort((a, b) => a.x - b.x);

          state.data.Datasets[idx].Data = merged;
        } else {
          state.data.Datasets.push(element);
        }
      });

      // update the start/end dates based on which direction we went
      if (direction === ADDITIONAL_DATA_DIRECTION.BACKWARD) {
        state.data.StartDate = action.payload.result.StartDate;
        state.startDate = {
          value: setMinutes(setHours(subDays(new Date(state.startDate.value), state.dataRange), 0), 0).getTime(),
          error: '',
          disabled: false,
        };
      } else if (direction === ADDITIONAL_DATA_DIRECTION.FORWARD) {
        state.data.EndDate = action.payload.result.EndDate;
        state.endDate = {
          value: setMinutes(setHours(addDays(new Date(state.endDate.value), state.dataRange), 23), 59).getTime(),
          error: '',
          disabled: false,
        };
      }

      // we may need to pop the Dataset from the state.data, if we have any Datasets left we
      // can add them and trigger the dropdown state change.
      // Wilson says this is unlikely to happen, but leaving this note in case

      state.status.additionalData = STATE_STATUS.SUCCESS;
    },
    [fetchAdditionalData.rejected]: (state) => {
      state.status.additionalData = STATE_STATUS.FAILED;
    },
    [fetchAdditionalMultiStationData.pending]: (state) => {
      state.status.additionalMultiStationData = STATE_STATUS.LOADING;
    },
    [fetchAdditionalMultiStationData.fulfilled]: (state, action) => {
      const direction = action.payload.direction;
      //join any unique weather stations Ids
      const updatedWxIds = Array.from(
        new Set([...state.multiStationData.WeatherStationIds], [...action.payload.result.WeatherStationIds]),
      );
      state.multiStationData.WeatherStationIds = updatedWxIds;

      //iterate thru the DTOs
      action.payload.result.DataVisualizationDTOs.forEach((dto) => {
        const weatherStationId = dto.WeatherStationId;
        const dtoIdx = state.multiStationData.DataVisualizationDTOs.findIndex((dv) => {
          return dv.WeatherStationId === weatherStationId;
        });

        //if the weather station dto didn't exist we can just straight up add it
        if (dtoIdx === -1) {
          state.multiStationData.DataVisualizationDTOs.push(dto);
        } else {
          //it exists so we need to go thru datasets and reconcile them
          dto.Datasets.forEach((ds) => {
            const datasetId = ds.DatasetId;
            const dsIdx = state.multiStationData.DataVisualizationDTOs[dtoIdx].Datasets.findIndex((dataset) => {
              return dataset.DatasetId === datasetId;
            });

            //if it doesn't exist we need to add it
            if (dsIdx === -1) {
              state.multiStationData.DataVisualizationDTOs[dtoIdx].Datasets.push(ds);
            } else {
              const data1 = state.multiStationData.DataVisualizationDTOs[dtoIdx].Datasets[dsIdx].Data || [];
              const data2 = ds.Data || [];
              const merged = data1.concat(data2).sort((a, b) => a.x - b.x);

              state.multiStationData.DataVisualizationDTOs[dtoIdx].Datasets[dsIdx].Data = merged;
            }
          });
        }
      });
      //iterate thru the action payload Datasets, find the corresponding Dataset in the state.data.Dataset

      // action.payload.result.Datasets.forEach((element) => {
      //   const payloadDatasetId = element.DatasetId;
      //   const idx = state.data.Datasets.findIndex((ds) => {
      //     return ds.DatasetId === payloadDatasetId;
      //   });
      //   // if the dataset from the payload doesn't exist then the idx is -1, in that case
      //   // we should add the dataset to the state.data
      //   if (idx !== -1) {
      //     const data1 = state.data.Datasets[idx].Data || [];
      //     const data2 = element.Data || [];
      //     const merged = data1.concat(data2).sort((a, b) => a.x - b.x);

      //     state.data.Datasets[idx].Data = merged;
      //   } else {
      //     state.data.Datasets.push(element);
      //   }
      // });

      // update the start/end dates based on which direction we went
      //if (direction === ADDITIONAL_DATA_DIRECTION.BACKWARD) {
      state.multiStationData.StartDate = action.payload.result.StartDate;
      state.multiStationData.EndDate = action.payload.result.EndDate;

      const sDate = action.payload.result.StartDate.split('-'); // year, mon, day, hour, min, sec
      const eDate = action.payload.result.EndDate.split('-'); // year, mon, day, hour, min, sec

      if (direction === ADDITIONAL_DATA_DIRECTION.BACKWARD) {
        state.startDate = {
          value: new Date(sDate[0], sDate[1] - 1, sDate[2], sDate[3], sDate[4], sDate[5]).getTime(),
          //value: setMinutes(setHours(new Date(state.startDate.value), 0), 0).getTime(),
          error: '',
          disabled: false,
        };
      } else if (direction === ADDITIONAL_DATA_DIRECTION.FORWARD) {
        //  state.multiStationData.EndDate = action.payload.result.EndDate;
        state.endDate = {
          value: new Date(eDate[0], eDate[1] - 1, eDate[2], eDate[3], eDate[4], eDate[5]).getTime(),
          //value: setMinutes(setHours(new Date(state.endDate.value), 23), 59).getTime(),
          error: '',
          disabled: false,
        };
      }

      state.status.additionalMultiStationData = STATE_STATUS.SUCCESS;
    },
    [fetchAdditionalMultiStationData.rejected]: (state) => {
      state.status.additionalMultiStationData = STATE_STATUS.FAILED;
    },
  },
});

//common function to filter and sort the datasets
const getFilteredDatasets = (selectedOptions, data, allWeatherStations = []) => {
  // let filteredDatasets = data.Datasets.filter((dataset) =>
  //   selectedOptions.find((option) => option.value === dataset.value),
  // );
  let filteredDatasets = selectedOptions
    .map((option) => {
      // Key filtering point- For multi-station graphs, both the options and datasets include the weatherStationId to select the right dataset
      // for single graph and datatable /manual validation, the options do not include the weatherStationId.
      let dataset = data.Datasets.find(
        (dataset) =>
          (option.weatherStationId > 0 &&
            dataset.weatherStationId == option.weatherStationId &&
            dataset.value === option.value) ||
          (!(option.weatherStationId > 0) && dataset.value === option.value),
      );

      let weatherStationInfo = option.weatherStationInfo;
      if (option.weatherStationInfo === undefined) {
        weatherStationInfo = allWeatherStations.find((entity) => entity.Id === option.weatherStationId);
      }

      return dataset
        ? {
            ...dataset,
            sensorType: option.sensorType,
            weatherStationInfo: {
              stationId: option.weatherStationId,
              text: option.weatherStationText,
              ...weatherStationInfo,
            },
          }
        : undefined;
    })
    .filter((obj) => obj !== undefined);

  return filteredDatasets;
};

export const getSelectedDatasetsForGraph = (selectedOptions, data, heatMapObservation = null, allWeatherStations) => {
  if (!data) return null;
  //if (!data.Datasets || !data.DataVisualizationDTOs.length === 0) return null;  //this is problematic for multi-visualization
  if (!selectedOptions) return null;
  let filteredDatasets = getFilteredDatasets(selectedOptions, data, allWeatherStations);
  //re-map the filtered datasets and find the associated option so that we can
  // add the selected color to it for use in the graph and legend
  filteredDatasets = filteredDatasets.map((ds) => {
    const opt = selectedOptions.find(
      (option) =>
        (option.weatherStationId > 0 && option.value === ds.value && option.weatherStationId == ds.weatherStationId) ||
        (!(option.weatherStationId > 0) && option.value === ds.value),
    );
    if (opt) return { ...ds, color: opt.color || '' };

    return ds; //return the dataset with no modification
  });
  const heatMapOpt = selectedOptions.find((opt) => opt.obstypeid === heatMapObservation?.ObservationTypeId);
  if (heatMapOpt) {
    const subSfcObsTypeId = heatMapOpt.value.split('_')[2];

    filteredDatasets.push(
      ...data.Datasets.filter(
        (dataset) => dataset.observationTypeId === parseInt(subSfcObsTypeId) && dataset.SensorLayerNumber !== null,
      ).map((d) => {
        return { ...d, color: heatMapOpt.color };
      }),
    );

    //sortFrostProbeTypeData(filteredDatasets, 'SortName'); // sort by relative elavation for all frost probe/snow pole components

    //push the pavement temp as part of the frost probe, that what represents 0cm
    const surfaceObs = data.Datasets.filter((dataset) => dataset.graphConfig?.observationTypeName === 'sfc_temp')[0];
    if (!isValueEmpty(surfaceObs)) {
      filteredDatasets.push({
        ...surfaceObs,
        DatasetId: `${surfaceObs.DatasetId}_${subSfcObsTypeId}`, //it needs to have a unique dataset id so we fake it
        SensorLayerNumber: 0,
        RelativeElevation: 0,
        rawElevation: 0,
        type: DATASET_TYPES.FrostProbe, // fake the e.type to be DATASET_TYPES.FrostProbe so it gets pulled with frost probes
        color: heatMapOpt.color,
      });
    }
  }

  return filteredDatasets;
};

export const getSelectedDatasetsByUserPreset = (selectedOptions, data, presets) => {
  if (!data) return null;
  if (!data.Datasets) return null;
  if (!selectedOptions) return null;

  let _selectedData = getSelectedDatasets(selectedOptions, data);
  if (!presets || presets.length === 0) return _selectedData;

  let _selectedPresetObsTypeIds = presets.flatMap((item) => {
    // Check if ObservationTypes is not null
    if (item && item.IsEnabled && item.ObservationTypes) {
      // Extract ObservationTypeId from each element in ObservationTypes
      return item.ObservationTypes.map((observation) => '_' + observation.ObservationTypeId + '_');
    }
    return [];
  });

  if (!_selectedPresetObsTypeIds || _selectedPresetObsTypeIds.length === 0) return _selectedData;

  _selectedData.map((option) => {
    return option.DatasetId;
  });
  return _selectedData.filter((data) => {
    // Check if any element of _selectedPresetObsTypeIds is included in DatasetId
    return _selectedPresetObsTypeIds.some((obsTypeId) => data.DatasetId.includes(obsTypeId));
  });
};

export const getSelectedDatasets = (selectedOptions, data) => {
  if (!data) return null;
  if (!data.Datasets) return null;
  if (!selectedOptions) return null;

  //call generic common function to filter and sort the datasets
  return getFilteredDatasets(selectedOptions, data);
};
// countObservationType - int- to count the datasets have the same observation type. If only one dataset for a OT, the rank will not be applied
export const getConvertedPrecedence = (
  dataSets,
  observationTypeId,
  sensorTypeId,
  modelSpecificationId,
  countObservationType,
) => {
  if (!dataSets) return null;

  const filteredData = dataSets.filter(
    (item) => item.observationTypeId === observationTypeId && item.SensorTypeId !== null && item.Precedence !== null,
  );

  if (filteredData.length < 1 || countObservationType <= 1) {
    return null;
  }
  // Sort by Precedence ascending
  filteredData.sort((a, b) => a.Precedence - b.Precedence);
  let lastPrecedence = null;
  let currentRank = 0;

  const rankedItems = filteredData.map((item) => {
    if (item.Precedence !== lastPrecedence) {
      currentRank += 1;
      lastPrecedence = item.Precedence;
    }
    return {
      ...item,
      ConvertedPrecedence: currentRank,
    };
  });

  // Find the rank of the target item
  const modItem =
    modelSpecificationId > 0
      ? rankedItems.find(
          (item) => item.SensorTypeId === sensorTypeId && item.ModelSpecificationId === modelSpecificationId,
        )
      : null;

  const target =
    modelSpecificationId > 0
      ? modItem || rankedItems.find((item) => item.SensorTypeId === sensorTypeId && !item.ModelSpecificationId)
      : rankedItems.find((item) => item.SensorTypeId === sensorTypeId && !item.ModelSpecificationId);

  return target ? target.ConvertedPrecedence : null;
};

export const addAttributesAndSort = (data, observationTypes, validationRules) => {
  if (!data) return;
  if (!data.Datasets) return;
  const wsId = data.WeatherStationId;
  const _datasets = data.Datasets.filter(
    ({ DatasetId, SensorTypeId, ModelSpecificationId, Precedence }) =>
      DatasetId !== null && SensorTypeId !== null && Precedence !== null,
  ).map((item, index) => {
    console.log(`Mapping index ${index}:`, item.DatasetId);
    const dstId = parseDatasetId(item.DatasetId);
    return {
      observationTypeId: dstId.observationTypeId,
      SensorTypeId: item.SensorTypeId,
      ModelSpecificationId: item.ModelSpecificationId,
      Precedence: item.Precedence,
    };
  });
  const convertedObsIds = data.Datasets.map((item) => {
    const parts = parseDatasetId(item.DatasetId);
    return parts.observationTypeId;
  });
  data.Datasets = data.Datasets.map((dataset) => {
    const dsId = parseDatasetId(dataset.DatasetId);
    const observationType = observationTypes.find((o) => o.ObservationTypeId == dsId.observationTypeId);
    const countObservationType =
      !_datasets || _datasets.length === 0
        ? 0
        : convertedObsIds.filter((otId) => {
            return otId === dsId.observationTypeId;
          }).length;
    //ObservationTypeName
    const rawlabel = isValueEmpty(dataset.ObservationTypeLabel)
      ? observationType.ObservationTypeDisplayName
      : dataset.ObservationTypeLabel;
    const precedence = getConvertedPrecedence(
      _datasets,
      dsId.observationTypeId,
      dataset.SensorTypeId,
      dataset.ModelSpecificationId,
      countObservationType,
    );
    dataset.weatherStationId = wsId;
    dataset.observationTypeId = dsId.observationTypeId;
    dataset.type = dsId.datasetType;
    dataset.value = dataset.DatasetId;
    dataset.rawlabel = rawlabel;
    dataset.elevation = dataset.RelativeElevation ?? 0;
    dataset.rawElevation = dataset.RelativeElevation; //rel elevation could be 0
    dataset.sensorTypeName = dataset.SensorTypeName;
    dataset.collectionIntervalMinutes = isValueEmpty(dataset.CollectionIntervalMinutes)
      ? 60
      : dataset.CollectionIntervalMinutes;
    dataset.group = dataset.PlacementName;
    dataset.label =
      dsId.datasetType === DATASET_TYPES.FrostProbe || dsId.datasetType === DATASET_TYPES.SnowPole
        ? `${rawlabel} ${convertRelElevationTocm(dataset.RelativeElevation) ?? ''}`
        : precedence
        ? `${rawlabel} (${precedence})`
        : rawlabel;
    dataset.unitLabel = observationType.YAxisUnitLabel;

    dataset.graphConfig =
      dsId.datasetType !== DATASET_TYPES.Image
        ? {
            //needed for graph dataset data items
            graphType: observationType.GraphType,
            transform: observationType.YAxisTransformType,
            graphAxisId: observationType.GraphAxisId,
            assocObsTypeId: observationType.YAxisAssociatedObservationTypeID,
            displayFormat: observationType.DisplayFormat,
            observationTypeName: observationType.ObservationTypeName,

            //needed for yAxis config
            yAxisTickList: observationType.YAxisTickList,
            yAxisMin: observationType.YAxisMin,
            yAxisMax: observationType.YAxisMax,
            yAxisMinFlex: observationType.YAxisMinFlex,
            yAxisMaxFlex: observationType.YAxisMaxFlex,
            graphAxisName: observationType.GraphAxisName,
            yAxisTickDecimalPlaces: observationType.YAxisTickDecimalPlaces,
            graphColor: observationType.DefaultGraphColor,
          }
        : {
            graphColor: observationType.DefaultGraphColor,
          };

    if (dataset.Data) {
      const depth = dataset.elevation;

      //need to sort data by x axis
      dataset.Data.sort((a, b) => {
        return a.x - b.x;
      });

      dataset.Data.forEach((cell) => {
        if (observationType.MeaningFullList) {
          const m = observationType.MeaningFullList.find((m) => m.OBSERVED_CHARACTER_VALUE === cell.y);
          cell.DisplayedMeaning = (m && m.DISPLAYED_MEANING) ?? '';
          cell.DisplayOrder = (m && m.DISPLAY_ORDER) ?? '';
        }

        cell.DisplayFormat = observationType.DisplayFormat;
        cell.Value = !cell.y ? null : cell.y;
        cell.IsInvalid = !isValueEmpty(cell.ConfidenceLevel) && cell.ConfidenceLevel < 0;
        cell.IsMissing = !cell.y;
        cell.IsNan = cell.y === 'NAN';
        cell.IsSpecial = cell.y === '-6999';
        cell.Depth = depth;

        if (!isValueEmpty(cell.ConfidenceLevel)) {
          cell.ConfidenceLevelName = validationRules.find(
            (v) => v.ConfidenceLevelRating === cell.ConfidenceLevel,
          )?.Name;
        }
      });
    }
    return dataset;
  }).sort(fieldSorter(['group', 'rawlabel', 'elevation', 'DatasetId']));
};

export const getDatasetDropdownOptions = (
  data,
  withImage,
  combineProps = { frostProbeHeatMapObs: false, snowPoleHeatMapObs: false, isGraph: false },
) => {
  if (!data) return [];
  if (!data.Datasets) return [];
  let source = withImage ? data.Datasets : data.Datasets.filter((dataset) => dataset.type !== DATASET_TYPES.Image);
  if (combineProps.isGraph) {
    //strip the non-graph datasets
    source = source.filter(
      (dataset) =>
        dataset &&
        (dataset.type === DATASET_TYPES.Image || (dataset.graphConfig && !isValueEmpty(dataset.graphConfig.graphType))),
    );
  }
  if (combineProps.frostProbeHeatMapObs) {
    //strip the frost probe datasets
    source = source.filter((dataset) => dataset.type !== DATASET_TYPES.FrostProbe);
  }

  const optionsWithGroup = [];
  let options = [];
  let curGroup = '';
  let parent = null;

  source.forEach((item, index) => {
    const option = {
      value: item.value,
      label: item.label,
      group: item.group,
      typeId: item.observationTypeId,
      rawElevation: item.rawElevation,
    };
    options.push(option);

    const itemGroupName = item.DatasetPrefix === '1' ? 'Images' : item.group;

    if (itemGroupName !== curGroup) {
      curGroup = itemGroupName;
      if (parent) optionsWithGroup.push(parent);
      parent = { label: curGroup, id: index, options: [option] };
    } else {
      parent.options.push(option);
      if (SENSOR_PLACEMENT_CONFIG.MAPPINGS.includes(item.sensorTypeName))
        parent.options = sortFrostProbeTypeDropDown([...parent.options]);
    }
  });

  if (parent) optionsWithGroup.push(parent);

  // we are combining the frost probe datasets

  if (combineProps.frostProbeHeatMapObs) {
    const subSfcObsTypeId =
      data.Datasets.find(
        (ds) => ds.type === DATASET_TYPES.FrostProbe && ds.graphConfig.observationTypeName === 'subsfc_temp',
      )?.observationTypeId || 0;
    if (subSfcObsTypeId !== 0) {
      //get distinct frost probe observation types from the datasets except subsfc_temp
      const distinctFPDatasets = data.Datasets.filter(
        (ds) =>
          ds.type === DATASET_TYPES.FrostProbe && ds.graphConfig.observationTypeName !== FROST_PROBE_CONFIG.SUBSFC_TEMP,
      )
        .map((ds) => {
          return {
            group: ds.group,
            typeId: ds.observationTypeId,
            label: ds.rawlabel,
            value: ds.DatasetId,
            rawElevation: null,
            typeName: ds.graphConfig.observationTypeName,
          };
        })
        .filter((v, i, s) => s.map((d) => d.typeId).indexOf(v.typeId) === i);

      const group = distinctFPDatasets.map((d) => d.group).filter((v, i, s) => s.indexOf(v) === i)[0] || 'Frost Probe';

      distinctFPDatasets.push({
        group: group,
        typeId: combineProps.frostProbeHeatMapObs.ObservationTypeId,
        label: combineProps.frostProbeHeatMapObs.ObservationTypeDisplayName,
        value: `0_${combineProps.frostProbeHeatMapObs.ObservationTypeId}_${subSfcObsTypeId}`, //FROST_PROBE_CONFIG.DATA_SOURCE,
        rawElevation: null,
        typeName: combineProps.frostProbeHeatMapObs.ObservationTypeName,
      });

      parent = {
        label: group,
        id: optionsWithGroup.length,
        options: distinctFPDatasets,
      };

      options = options.concat(distinctFPDatasets);
      optionsWithGroup.push(parent);
    }
  }

  optionsWithGroup.sort((a, b) => {
    return a.label < b.label ? -1 : 1;
  });

  return { optionsWithGroup, options };
};

export const {
  setPreset,
  clearPreset,
  setWeatherStation,
  clearWeatherStation,
  clearData,
  setStartDate,
  setEndDate,
  setDataDateRange,
  setDates,
  setSelectedDatasets,
  setShowConfidenceLevel,
  setShowInvalidData,
  setShowValidData,
  setWeatherForecastId,
  setIssuedBy,
  setWeatherForecasts,
  setTextDescription,
  setDataRange,
  setDisplayDateRange,
  setViewableGraphDates,
} = dataVisualizationSlice.actions;
